import React, { useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
    Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

export default function BookingPublicForm() {
    const [formData, setFormData] = useState({
        name: '',
        gender: '',
        birthDate: '',
        idNumber: '',
        email: '',
        contact: '',
        location: '',
        preferredDate: '',
        timeSlot: '',
        bodyPart: '',
        notes: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    // Prefill from URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const prefillData = { ...formData };

        if (params.get('name')) prefillData.name = params.get('name');
        if (params.get('preferredDate')) prefillData.preferredDate = params.get('preferredDate');
        if (params.get('timeSlot')) prefillData.timeSlot = params.get('timeSlot');
        if (params.get('bodyPart')) prefillData.bodyPart = params.get('bodyPart');
        if (params.get('location')) prefillData.location = params.get('location');

        setFormData(prefillData);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // 計算年齡
            let age = null;
            if (formData.birthDate) {
                const birthDate = new Date(formData.birthDate);
                const diffMs = Date.now() - birthDate.getTime();
                const ageDate = new Date(diffMs);
                age = Math.abs(ageDate.getUTCFullYear() - 1970);
            }

            // 檢查 client 是否已存在
            const q = query(
                collection(db, 'clients'),
                where('idNumber', '==', formData.idNumber)
            );
            const snap = await getDocs(q);

            let clientId;
            if (!snap.empty) {
                clientId = snap.docs[0].id;
            } else {
                const clientRef = await addDoc(collection(db, 'clients'), {
                    name: formData.name,
                    gender: formData.gender,
                    birthDate: formData.birthDate,
                    age: age || '',
                    idNumber: formData.idNumber,
                    email: formData.email,
                    contact: formData.contact,
                    location: formData.location,
                    createdAt: serverTimestamp(),
                });
                clientId = clientRef.id;
            }

            // assign therapist
            const assignedTherapist = await assignTherapist();

            // ⏱ prepare sessionDateTime
            let sessionDateTime = null;
            if (formData.preferredDate && formData.timeSlot) {
                const str = `${formData.preferredDate}T${formData.timeSlot}:00`;
                sessionDateTime = Timestamp.fromDate(new Date(str));
            }

            // prepare session data
            const sessionData = {
                date: formData.preferredDate,
                timeSlot: formData.timeSlot,
                bodyPart: formData.bodyPart,
                note: formData.notes || '',
                createdAt: serverTimestamp(),
                therapistUID: assignedTherapist?.therapistUID || '',
                therapistName: assignedTherapist?.therapistName || '',
                source: 'external',
                sessionDateTime: sessionDateTime,
            };

            // add session under client
            await addDoc(
                collection(db, 'clients', clientId, 'sessions'),
                sessionData
            );

            // add to global sessions
            await addDoc(collection(db, 'sessions'), {
                ...sessionData,
                clientName: formData.name,
                clientId,
            });

            // Optionally keep record in bookingPublic
            await addDoc(collection(db, 'bookingPublic'), {
                ...formData,
                createdAt: serverTimestamp(),
            });

            navigate('/booking-confirmation', {
                state: {
                    booking: {
                        ...formData,
                        therapistName: assignedTherapist?.therapistName || '',
                    },
                },
            });
        } catch (err) {
            console.error(err);
            setError('提交失敗，請稍後再試。');
        } finally {
            setLoading(false);
        }
    };

    const assignTherapist = async () => {
        const rolesSnap = await getDocs(
            query(collection(db, 'roles'), where('role', '==', 'student'))
        );

        const students = rolesSnap.docs.map((doc) => ({
            therapistUID: doc.id,
            therapistName: doc.data().displayName || 'Unnamed',
        }));

        let minCount = Infinity;
        let selected = null;

        for (const s of students) {
            const sessionSnap = await getDocs(
                query(collection(db, 'sessions'), where('therapistUID', '==', s.therapistUID))
            );
            if (sessionSnap.size < minCount) {
                minCount = sessionSnap.size;
                selected = s;
            }
        }

        return selected;
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
            <form
                onSubmit={handleSubmit}
                className="bg-white p-6 rounded shadow w-full max-w-lg space-y-4"
            >
                <h2 className="text-xl font-semibold text-center">
                    📅 預約查詢表格
                </h2>

                <input
                    type="text"
                    name="name"
                    placeholder="你的姓名"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                />

                <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                >
                    <option value="">選擇性別</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                    <option value="其他">其他</option>
                </select>

                <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="text"
                    name="idNumber"
                    placeholder="身份證號碼 (只用作核對身份)"
                    value={formData.idNumber}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="email"
                    name="email"
                    placeholder="電子郵件（如需聯絡）"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="text"
                    name="contact"
                    placeholder="聯絡方式（電話 / WhatsApp）"
                    value={formData.contact}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="text"
                    name="location"
                    placeholder="居住地址"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className="w-full border px-4 py-2 rounded"
                />

                <input
                    type="text"
                    name="timeSlot"
                    placeholder="時間時段"
                    value={formData.timeSlot}
                    readOnly
                    className="w-full border px-4 py-2 rounded bg-gray-100 cursor-not-allowed"
                />

                <input
                    type="text"
                    name="bodyPart"
                    placeholder="想接受治療的身體部分"
                    value={formData.bodyPart}
                    onChange={handleChange}
                    required
                    className="w-full border px-4 py-2 rounded"
                />

                <textarea
                    name="notes"
                    placeholder="有冇想講俾我地知嘅情況？（選填）"
                    value={formData.notes}
                    onChange={handleChange}
                    className="w-full border px-4 py-2 rounded"
                />

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {loading ? '提交中...' : '📨 提交預約查詢'}
                </button>

                {error && (
                    <p className="text-red-600 text-center">{error}</p>
                )}
            </form>
        </div>
    );
}
